import './editorStyles.css';

import { Color } from '@tiptap/extension-color';
import ListItem from '@tiptap/extension-list-item';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useCallback, useEffect } from 'react';
import { Grid, IconButton, Tooltip } from '@mui/material';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import { ReactComponent as BoldIcon } from '../../assets/images/NewDocumentation/BoldIcon.svg';
import { ReactComponent as ItalicIcon } from '../../assets/images/NewDocumentation/ItalicIcon.svg';
import { ReactComponent as StrikeIcon } from '../../assets/images/NewDocumentation/StrikeIcon.svg';
import { ReactComponent as UnderlineIcon } from '../../assets/images/NewDocumentation/UnderlineIcon.svg';
import { ReactComponent as UnorderedListIcon } from '../../assets/images/NewDocumentation/UnorderedListIcon.svg';
import { ReactComponent as OrderedListIcon } from '../../assets/images/NewDocumentation/OrderedListIcon.svg';
import { ReactComponent as CodeIcon } from '../../assets/images/NewDocumentation/CodeIcon.svg';
import { ReactComponent as ClearNodeIcon } from '../../assets/images/NewDocumentation/ClearNodeIcon.svg';
import { ReactComponent as ParagraphIcon } from '../../assets/images/NewDocumentation/ParagraphIcon.svg';

const DEBOUNCE_DELAY = 100;
const DEBOUNCE_DELAY_COLOR = 50;

function MenuBar({ editor }) {
  const debouncedUpdateColor = useCallback(
    debounce((color) => {
      editor.chain().focus().setColor(color).run();
    }, DEBOUNCE_DELAY_COLOR),
    [editor]
  );

  const boldHandle = useCallback(
    () => editor.chain().focus().toggleBold().run(),
    [editor]
  );
  const italicHandle = useCallback(
    () => editor.chain().focus().toggleItalic().run(),
    [editor]
  );
  const strikeHandle = useCallback(
    () => editor.chain().focus().toggleStrike().run(),
    [editor]
  );
  const underlineHandle = useCallback(
    () => editor.chain().focus().toggleUnderline().run(),
    [editor]
  );
  const bulletListHandle = useCallback(
    () => editor.chain().focus().toggleBulletList().run(),
    [editor]
  );
  const orderedListHandle = useCallback(
    () => editor.chain().focus().toggleOrderedList().run(),
    [editor]
  );
  const codeHandle = useCallback(
    () => editor.chain().focus().toggleCodeBlock().run(),
    [editor]
  );

  const clearHandle = useCallback(
    () => editor.chain().focus().unsetAllMarks().run(),
    [editor]
  );

  const paragraphHandle = useCallback(
    () => editor.chain().focus().setParagraph().run(),
    [editor]
  );

  if (!editor) {
    return null;
  }

  return (
    <Grid position="sticky" display="flex" gap={2} className="menuBarTiptap">
      <Tooltip title="Bold">
        <IconButton
          size="small"
          onClick={boldHandle}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
        >
          <BoldIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Italic">
        <IconButton
          size="small"
          onClick={italicHandle}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
        >
          <ItalicIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Strike">
        <IconButton
          size="small"
          onClick={strikeHandle}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
        >
          <StrikeIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Paragraph">
        <IconButton
          size="small"
          onClick={paragraphHandle}
          className={editor.isActive('paragraph') ? 'is-active' : ''}
        >
          <ParagraphIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Underline">
        <IconButton
          size="small"
          onClick={underlineHandle}
          className={editor.isActive('underline') ? 'is-active' : ''}
        >
          <UnderlineIcon />
        </IconButton>
      </Tooltip>
      <input
        id="colorInput"
        type="color"
        onInput={(event) => debouncedUpdateColor(event.target.value)}
        value={editor.getAttributes('textStyle').color}
        data-testid="setColor"
      />
      <Tooltip title="Unordered List">
        <IconButton
          size="small"
          onClick={bulletListHandle}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
        >
          <UnorderedListIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Ordered List">
        <IconButton
          size="small"
          onClick={orderedListHandle}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
        >
          <OrderedListIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Code">
        <IconButton
          size="small"
          onClick={codeHandle}
          className={editor.isActive('codeBlock') ? 'is-active' : ''}
        >
          <CodeIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Clear Node">
        <IconButton size="small" onClick={clearHandle}>
          <ClearNodeIcon />
        </IconButton>
      </Tooltip>
    </Grid>
  );
}

MenuBar.propTypes = {
  editor: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

function CommentEditor({
  content,
  setContent,
  height,
  clearContentWithReset,
  setClearContetWithReset,
}) {
  // used this to improve fast typing performance of editor
  const debouncedSetContent = useCallback(
    debounce((html) => setContent(html), DEBOUNCE_DELAY),
    []
  );

  const extensions = [
    Color.configure({ types: [TextStyle.name, ListItem.name] }),
    TextStyle.configure({ types: [ListItem.name] }),
    StarterKit.configure({
      bulletList: {
        keepMarks: true,
        keepAttributes: false,
      },
      orderedList: {
        keepMarks: true,
        keepAttributes: false,
      },
    }),
    Underline,
  ];

  const editorInstance = useEditor({
    extensions,
    content,
    onUpdate: ({ editor }) => {
      debouncedSetContent(editor.getHTML());
    },
  });

  const clearContentInEditor = useCallback(() => {
    editorInstance.commands.clearContent(true);
  }, [editorInstance]);

  useEffect(() => {
    if (clearContentWithReset) {
      clearContentInEditor();
      setClearContetWithReset(false);
    }
  }, [clearContentWithReset]);

  return (
    <Grid sx={{ background: (theme) => theme.palette.other.white }}>
      <Grid
        p={1}
        sx={(theme) => ({
          position: 'sticky',
          top: 0,
          background: theme.palette.other.white,
          borderTop: '1px solid #C7C7C7',
          borderBottom: '1px solid #C7C7C7',
          zIndex: 100,
        })}
      >
        <MenuBar editor={editorInstance} />
      </Grid>
      <Grid overflow="auto" p={1} sx={{ background: 'white', height }}>
        <EditorContent editor={editorInstance} />
      </Grid>
    </Grid>
  );
}

CommentEditor.propTypes = {
  content: PropTypes.string.isRequired,
  setContent: PropTypes.func.isRequired,
  height: PropTypes.string.isRequired,
  clearContentWithReset: PropTypes.bool.isRequired,
  setClearContetWithReset: PropTypes.func.isRequired,
};

export default CommentEditor;
